import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArchiveRestore, ChevronLeft, ChevronRight, Trash2, Users } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../../components/ConfirmDialog';
import { PAGE_WIDE, DataTable, SearchInput, OverflowMenu, PersonAvatar, fmtDate, num, plural } from '../../components/ui';
import { roleLabel, forgetStaffRecord } from './hrKit';

const fullName = (s) => `${s.first_name || ''} ${s.last_name || ''}`.trim();

export default function StaffArchived() {
  const confirm = useConfirm();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setLoadError(null);
    api.get('/staff?is_active=false')
      .then((r) => { const d = r.data; setStaff(Array.isArray(d) ? d : (d.staff || d.data || [])); })
      .catch((e) => setLoadError(e.response?.data?.message || "We couldn't load archived staff. Try again."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, pageSize]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter((s) => `${fullName(s)} ${s.email || ''} ${s.phone || ''} ${s.address || ''}`.toLowerCase().includes(q));
  }, [staff, search]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(start, start + pageSize);

  const restore = async (s) => {
    if (!await confirm(`${fullName(s)} goes back on the active staff list and can be rostered again.`, { title: `Restore ${fullName(s)}?`, confirmLabel: 'Restore' })) return;
    setBusyId(s.id);
    try {
      await api.put(`/staff/${s.id}`, { isArchived: false });
      forgetStaffRecord(s.id);
      toast.success(`${fullName(s)} restored`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "They weren't restored. Try again."); }
    finally { setBusyId(null); }
  };
  const removeForever = async (s) => {
    if (!await confirm(
      `This removes ${fullName(s)} and ALL their records: shifts, timesheets, qualifications, documents and notes. It can't be undone.`,
      { title: `Permanently delete ${fullName(s)}?`, confirmLabel: 'Delete permanently', danger: true },
    )) return;
    setBusyId(s.id);
    try {
      await api.delete(`/staff/${s.id}/permanent`);
      forgetStaffRecord(s.id);
      toast.success(`${fullName(s)} permanently deleted`);
      load();
    } catch (e) { toast.error(e.response?.data?.message || "They weren't deleted. Try again."); }
    finally { setBusyId(null); }
  };

  const columns = [
    { key: 'name', header: 'Name', sortable: true, sortValue: (s) => fullName(s).toLowerCase(), render: (s) => (
      <span className="flex items-center gap-3">
        <PersonAvatar id={s.id} photo={s.profile_photo_url} firstName={s.first_name} lastName={s.last_name} size="sm" />
        <Link to={`/staff/${s.id}`} className="font-medium text-slate-900 hover:text-primary-700 hover:underline">{fullName(s)}</Link>
      </span>
    ) },
    { key: 'role', header: 'Role', sortable: true, sortValue: (s) => roleLabel(s.role), render: (s) => roleLabel(s.role) },
    { key: 'phone', header: 'Phone', render: (s) => (s.phone ? <span className="whitespace-nowrap tabular-nums">{s.phone}</span> : <span className="text-slate-400">—</span>) },
    { key: 'email', header: 'Email', className: 'max-w-[220px]', render: (s) => (s.email ? <span className="block truncate" title={s.email}>{s.email}</span> : <span className="text-slate-400">—</span>) },
    { key: 'address', header: 'Address', className: 'max-w-[220px]', render: (s) => (s.address ? <span className="block truncate" title={s.address}>{s.address}</span> : <span className="text-slate-400">—</span>) },
    { key: 'updated', header: 'Last changed', sortable: true, defaultDir: 'desc', sortValue: (s) => s.archived_at || s.updated_at || null,
      render: (s) => <span className="whitespace-nowrap tabular-nums text-slate-600" title="When the record was last changed, which is usually when it was archived">{(s.archived_at || s.updated_at) ? fmtDate(s.archived_at || s.updated_at) : '—'}</span> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', render: (s) => (
      <span className="flex items-center justify-end gap-1">
        <button type="button" onClick={() => restore(s)} disabled={busyId === s.id} className="btn-secondary btn-sm"><ArchiveRestore size={13} aria-hidden="true" />Restore</button>
        <OverflowMenu label={`More actions for ${fullName(s)}`} items={[{ label: 'Delete permanently', icon: Trash2, danger: true, onClick: () => removeForever(s), disabled: busyId === s.id }]} />
      </span>
    ) },
  ];

  return (
    <div className={PAGE_WIDE}>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-3">
          <Link to="/staff" className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-500 hover:text-slate-800"><ChevronLeft size={14} aria-hidden="true" />Active staff</Link>
          <span className="text-[13px] text-slate-600">{loading ? 'Loading…' : plural(staff.length, 'archived staff member')}</span>
          <SearchInput value={search} onChange={setSearch} placeholder="Search name, email, phone or address" className="w-full sm:ml-auto sm:w-72" />
        </div>
        <DataTable
          label="Archived staff"
          columns={columns}
          rows={pageRows}
          loading={loading}
          error={loadError}
          onRetry={load}
          empty={staff.length === 0
            ? { icon: Users, title: 'No archived staff', hint: 'When you archive someone from their profile they move here, and you can restore them any time.' }
            : { icon: Users, title: 'No one matches', hint: 'Try another search.', action: <button type="button" onClick={() => setSearch('')} className="btn-secondary btn-sm">Clear search</button> }}
        />
        {!loading && !loadError && filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-[13px] text-slate-600">
            <div className="flex flex-wrap items-center gap-2">
              <span className="tabular-nums">{num(start + 1)}–{num(Math.min(start + pageSize, filtered.length))} of {num(filtered.length)}</span>
              <label className="inline-flex items-center gap-1.5"><span className="text-slate-500">Rows per page</span>
                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[13px]">
                  {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage === 1} className="btn-secondary btn-sm" aria-label="Previous page"><ChevronLeft size={14} aria-hidden="true" /></button>
              <span className="tabular-nums">{safePage} / {totalPages}</span>
              <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="btn-secondary btn-sm" aria-label="Next page"><ChevronRight size={14} aria-hidden="true" /></button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
